import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import Icon from '@/components/atoms/Icon'
import { MentorsModel } from '@models'
import sanitizeHtml from 'sanitize-html'
import normalizeLinkedinUrl from '@/utils/helpers/normalizeLinkedinUrl'

interface CardMentorProps {
	isLoading?: boolean
	className?: string
	onClick?: () => void
}

function formatMentorJob({
	companyRole,
	companyName,
	verticals = [],
}: {
	companyRole: string
	companyName?: string
	verticals: Array<string>
}) {
	let vertical

	switch (verticals[0]) {
		case 'CONSULTANT': {
			vertical = 'Carreira'
			break
		}
		case 'DS': {
			vertical = 'Data Science'
			break
		}
		case 'PM': {
			vertical = 'Product Management'
			break
		}
		case 'WD': {
			vertical = 'Web Development'
			break
		}
		case 'UX': {
			vertical = 'Design'
			break
		}
		case 'SS': {
			vertical = 'Inovação e Gestão'
			break
		}
		case 'MKT': {
			vertical = 'Marketing'
			break
		}
		default: {
			vertical = ''
			break
		}
	}

	let verticalText = vertical ? `de ${vertical}` : ''

	if (verticalText === 'de CONSULTANT') verticalText = 'de Carreira'

	return companyRole && companyName
		? `${companyRole} at ${companyName}`
		: companyRole || companyName || `Mentor(a) ${verticalText} na Awari`
}

export function CardMentor({
	mentor,
	className,
	onClick,
	isLoading,
}: { mentor: MentorsModel.Show } & CardMentorProps) {
	const linkedinUrl = normalizeLinkedinUrl(mentor?.linkedin)
	const title = `${mentor.name} ${
		mentor.lastName ? `${mentor.lastName[0].toUpperCase()}.` : ''
	}`

	const subtitle = formatMentorJob({
		companyRole: mentor?.companyRole,
		companyName: mentor?.companyName,
		verticals: mentor?.verticals,
	})

	const sanitizedBio = mentor.bio
		? sanitizeHtml(mentor.bio, { allowedTags: [] })
		: (`${mentor.name} é mentor(a) ${mentor.vertical ? `especialista em ${mentor.vertical}` : ''} na Awari` as unknown as TrustedHTML)

	return (
		<Link
			to={`/mentorias/${mentor?.slug}`}
			className={twMerge(
				'flex bg-zinc-900 hover:bg-zinc-900/50 w-full xl:max-w-[357px] cursor-pointer flex-col shadow-course rounded-md py-6 px-5 pb-8 transition-colors duration-300',
				className
			)}
			onClick={onClick}
		>
			<div className='flex flex-col gap-3 h-full'>
				<div className='flex w-max relative'>
					{(() => {
						if (mentor?.profilePicture) {
							return (
								<img
									src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.profilePicture}`}
									alt='Avatar Mentor'
									className='min-w-[100px] min-h-[100px] max-w-[100px] max-h-[100px] object-fill border-4 xl:border border-gray-100 dark:border-[#131920] rounded-full'
								/>
							)
						}

						return (
							<Icon
								name='default-avatar'
								width='100'
								height='100'
								className='min-w-[100px] min-h-[100px] max-w-[100px] max-h-[100px] object-fill border-4 xl:border border-gray-100 dark:border-[#131920] rounded-full'
							/>
						)
					})()}

					{(() => {
						if (mentor?.companyPicture) {
							return (
								<img
									src={`${process.env.REACT_APP_IMAGE_PROFILE_URL}${mentor?.companyPicture}`}
									alt='Avatar Company Mentor'
									className='min-w-[46px] min-h-[46px] max-w-[46px] max-h-[46px] absolute -bottom-1 -right-4 border-2 object-fill border-gray-50 dark:border-[#131920] rounded-full'
								/>
							)
						}

						return (
							<Icon
								name='default-avatar'
								width='46'
								height='46'
								className='min-w-[46px] min-h-[46px] max-w-[46px] max-h-[46px] absolute -bottom-1 -right-4 border-2 object-fill border-gray-50 dark:border-[#131920] rounded-full'
							/>
						)
					})()}
				</div>
				<div className='flex flex-col gap-2 h-full'>
					<div className='flex flex-col gap-3'>
						<div className='flex flex-col gap-1'>
							<p className='text-2xl font-medium text-white'>
								{title}
							</p>
							<span className='text-sm leading-[155%] font-bold text-zinc-50 max-w-[273px]'>
								{subtitle}
							</span>
						</div>
						{linkedinUrl && (
							<a
								href={linkedinUrl}
								target='_blank'
								rel='noopener noreferrer'
								onClick={(event) => event.stopPropagation()}
								className='inline-flex w-max items-center gap-2 rounded-full border border-primary-medium bg-white px-4 py-2 text-sm font-bold leading-[155%] text-primary-medium transition-all duration-300 hover:bg-white hover:text-primary-medium hover:shadow-sm dark:border-primary-medium dark:bg-white'
							>
								<Icon
									name='linkedin'
									width='16'
									height='16'
									fill='currentColor'
									className='min-w-[16px] min-h-[16px] text-current'
								/>
								Ver LinkedIn
							</a>
						)}
					</div>
					<div className='flex flex-col gap-5 h-full justify-between'>
						<p
							className='text-base leading-[155%] line-clamp-4 text-white/70 sm:max-w-[273px]'
							dangerouslySetInnerHTML={{ __html: sanitizedBio }}
						/>
						<span
							className={classNames(
								'text-base leading-[155%] text-white font-bold',
								{
									'animate-pulse': isLoading,
								}
							)}
						>
							Agendar mentoria →
						</span>
					</div>
				</div>
			</div>
		</Link>
	)
}
